import React, { useState } from 'react';
import { AppBar, Toolbar, Avatar, Typography, Menu, MenuItem, Box, ListItemIcon, ListItemText, Button, IconButton, Drawer, List, ListItem, ListItemButton } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { signOutUser } from '../config/Auth';
import { useNavigate } from 'react-router-dom';
import { Logout, PersonOutline, EventNote, Menu as MenuIcon, Forum as TownSquareIcon, Event as CampaignScheduleIcon, Book as BennyJournalIcon, DynamicFeed as CampaignEpisodesIcon, Map as MapIcon, Settings as SettingsIcon, EmojiEvents as TrophyIcon, AdminPanelSettings as AdminPanelSettingsIcon, LibraryBooks as WikiIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchBar from './SearchBar';

const Navbar = () => {
  const { currentUser, firestoreUser } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [campaignAnchorEl, setCampaignAnchorEl] = useState(null);
  const campaignMenuOpen = Boolean(campaignAnchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Check if user is admin
  const isAdmin = firestoreUser?.roles?.admin;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCampaignMenuOpen = (event) => {
    setCampaignAnchorEl(event.currentTarget);
  };

  const handleCampaignMenuClose = () => {
    setCampaignAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
      handleMenuClose();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  if (!currentUser) {
    return null;
  }

  const navbarBg = '#333333';
  const importantText = '#d98d2a';
  const generalText = '#F5F5F5';
  const secondaryGeneralText = '#A9A9A9';
  const menuBg = '#444444';
  const menuHoverBg = '#555555';

  const drawerWidth = 240;

  const navItems = [
    {
      text: 'Campaign',
      id: 'campaigns-button',
      icon: <MapIcon />,
      children: [
        { text: 'Campaign Schedule', path: '/campaign-schedule', icon: <CampaignScheduleIcon sx={{ color: importantText }} /> },
        { text: 'Campaign Episodes', path: '/campaign-episodes', icon: <CampaignEpisodesIcon sx={{ color: importantText }} /> },
      ]
    },
    { text: 'Conference Clamp', path: '/town-square', icon: <TownSquareIcon /> },
    { text: "Benny's Journal", path: '/benny-journal', icon: <BennyJournalIcon /> },
    { text: 'Campaign Maps', path: '/maps', icon: <MapIcon /> },
    { text: 'Trophy Hall', path: '/trophy-hall', icon: <TrophyIcon /> },
    { text: 'Wiki', path: '/wiki', icon: <WikiIcon /> },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ backgroundColor: navbarBg, height: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        py: 3,
        borderBottom: `1px solid ${menuHoverBg}`
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            color: importantText, 
            fontWeight: 'bold',
            fontFamily: '"Georgia", serif',
            letterSpacing: '-0.5px',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
            cursor: 'pointer'
          }} 
          onClick={() => navigate('/')}
        >
          🎲 Knuckleheads
        </Typography>
      </Box>
      <List sx={{ px: 1, py: 2 }}>
        {navItems.map((item) => (
          item.children ? (
            <React.Fragment key={item.text}>
              <ListItemButton 
                onClick={handleCampaignMenuOpen}
                aria-controls={campaignMenuOpen ? 'campaign-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={campaignMenuOpen ? 'true' : undefined}
                sx={{ 
                  color: generalText, 
                  borderRadius: '8px',
                  mb: 0.5,
                  py: 1.5,
                  '&:hover': { 
                    backgroundColor: 'rgba(217, 141, 42, 0.1)',
                    color: importantText
                  }
                }}
              >
                <ListItemIcon sx={{ color: importantText, minWidth: '40px' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: 'medium',
                    fontSize: '0.95rem'
                  }}
                />
              </ListItemButton>
            </React.Fragment>
          ) : (
            <ListItem key={item.text} disablePadding onClick={() => navigate(item.path)}>
              <ListItemButton sx={{ 
                color: generalText, 
                borderRadius: '8px',
                mb: 0.5,
                py: 1.5,
                '&:hover': { 
                  backgroundColor: 'rgba(217, 141, 42, 0.1)',
                  color: importantText
                }
              }}>
                <ListItemIcon sx={{ color: importantText, minWidth: '40px' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: 'medium',
                    fontSize: '0.95rem'
                  }}
                />
              </ListItemButton>
            </ListItem>
          )
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      elevation={3}
      sx={{ 
        background: `linear-gradient(135deg, ${navbarBg} 0%, #2a2a2a 100%)`,
        top: 0, 
        zIndex: 1100,
        borderBottom: `1px solid rgba(217, 141, 42, 0.2)`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
      }}
    >
      <Toolbar sx={{ 
        justifyContent: 'space-between',
        minHeight: { xs: 64, sm: 70 },
        px: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, color: generalText }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mr: 4,
              px: 1,
              py: 0.5,
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(217, 141, 42, 0.1)',
                transform: 'translateY(-1px)'
              }
            }}
            onClick={() => navigate('/')}
          >
            <Typography 
              variant="h5" 
              component="div" 
              sx={{ 
                color: importantText, 
                fontWeight: 'bold',
                fontFamily: '"Georgia", serif',
                letterSpacing: '-0.5px',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
            >
              🎲 Knuckleheads
            </Typography>
          </Box>
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {navItems.map((item) => (
                item.children ? (
                  <Button
                    key={item.text}
                    id={item.id}
                    aria-controls={campaignMenuOpen ? 'campaign-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={campaignMenuOpen ? 'true' : undefined}
                    onClick={handleCampaignMenuOpen}
                    startIcon={item.icon}
                    sx={{ 
                      color: generalText, 
                      textTransform: 'none',
                      fontWeight: 'medium',
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        backgroundColor: 'rgba(217, 141, 42, 0.1)',
                        color: importantText,
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {item.text}
                  </Button>
                ) : (
                  <Button 
                    key={item.text} 
                    startIcon={item.icon}
                    sx={{ 
                      color: generalText, 
                      textTransform: 'none',
                      fontWeight: 'medium',
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        backgroundColor: 'rgba(217, 141, 42, 0.1)',
                        color: importantText,
                        transform: 'translateY(-1px)'
                      }
                    }} 
                    onClick={() => navigate(item.path)}
                  >
                    {item.text}
                  </Button>
                )
              ))}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: isMobile ? 0 : 1, maxWidth: isMobile ? '100%' : 500, mx: 'auto' }}>
          <SearchBar isMobile={isMobile} />
        </Box>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer', 
            padding: '6px 12px',
            borderRadius: '12px',
            border: `1px solid transparent`,
            transition: 'all 0.2s ease',
            '&:hover': { 
              backgroundColor: 'rgba(217, 141, 42, 0.1)',
              borderColor: 'rgba(217, 141, 42, 0.3)',
              transform: 'translateY(-1px)'
            }
          }} 
          onClick={handleMenuOpen}
        >
          <Avatar 
            src={currentUser.photoURL} 
            alt={currentUser.displayName || 'User'} 
            sx={{ 
              width: 36, 
              height: 36, 
              mr: 1.5, 
              border: `2px solid ${importantText}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }} 
            imgProps={{ referrerPolicy: "no-referrer" }}
          />
          {!isMobile && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mr: 0.5 }}>
              <Typography variant="body2" component="div" sx={{ color: generalText, lineHeight: 1.3, fontWeight: '500' }}>
                {currentUser.displayName || currentUser.email.split('@')[0]}
              </Typography>
              <Typography variant="caption" component="div" sx={{ color: secondaryGeneralText, lineHeight: 1.2, fontSize: '0.7rem' }}>
                {currentUser.email}
              </Typography>
            </Box>
          )}
        </Box>        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'account-button',
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.3))',
              mt: 1.5,
              borderRadius: '8px',
              backgroundColor: menuBg, 
              color: generalText, 
              '& .MuiListItemIcon-root': {
                color: importantText, 
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: menuBg, 
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
        >
          <MenuItem 
            onClick={() => { handleMenuClose(); navigate('/my-characters'); }}
            sx={{ paddingY: 1.5, paddingX: 2, '&:hover': { backgroundColor: menuHoverBg } }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: importantText }}> {/* Ensure icon color matches */}
              <PersonOutline fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{variant: 'body2'}}>My Characters</ListItemText>
          </MenuItem>
          <MenuItem 
            onClick={() => { handleMenuClose(); navigate('/scheduling-prefs'); }}
            sx={{ paddingY: 1.5, paddingX: 2, '&:hover': { backgroundColor: menuHoverBg } }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: importantText }}> {/* Ensure icon color matches */}
              <EventNote fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{variant: 'body2'}}>Scheduling Preferences</ListItemText>
          </MenuItem>
          <MenuItem 
            onClick={() => { handleMenuClose(); navigate('/account'); }}
            sx={{ paddingY: 1.5, paddingX: 2, '&:hover': { backgroundColor: menuHoverBg } }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: importantText }}>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{variant: 'body2'}}>Account Settings</ListItemText>
          </MenuItem>
          {isAdmin && (
            <MenuItem 
              onClick={() => { handleMenuClose(); navigate('/admin'); }}
              sx={{ 
                paddingY: 1.5, 
                paddingX: 2, 
                backgroundColor: 'rgba(217, 141, 42, 0.1)',
                borderTop: `1px solid ${menuHoverBg}`,
                '&:hover': { backgroundColor: 'rgba(217, 141, 42, 0.2)' } 
              }}
            >
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: importantText }}>
                <AdminPanelSettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Admin Panel" 
                primaryTypographyProps={{variant: 'body2', fontWeight: 'bold'}}
              />
            </MenuItem>
          )}
          <MenuItem onClick={handleLogout} sx={{ paddingY: 1.5, paddingX: 2, '&:hover': { backgroundColor: menuHoverBg } }}>
            <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: importantText }}> {/* Ensure icon color matches */}
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{variant: 'body2'}}>Logout</ListItemText>
          </MenuItem>
        </Menu>

        {/* Campaigns Menu */}
        <Menu
          id="campaign-menu"
          anchorEl={campaignAnchorEl}
          open={campaignMenuOpen}
          onClose={handleCampaignMenuClose}
          MenuListProps={{
            'aria-labelledby': 'campaigns-button',
          }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.3))',
              mt: 1.5,
              borderRadius: '8px',
              backgroundColor: menuBg, 
              color: generalText,
              '& .MuiListItemIcon-root': {
                color: importantText,
              },              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 14,
                width: 10,
                height: 10,
                bgcolor: menuBg,
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
        >
          {navItems.find(item => item.id === 'campaigns-button')?.children.map((childItem) => (
            <MenuItem 
              key={childItem.text}
              onClick={() => {
                handleCampaignMenuClose();
                navigate(childItem.path);
              }}
              sx={{ paddingY: 1.5, paddingX: 2, '&:hover': { backgroundColor: menuHoverBg } }}
            >
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                {childItem.icon}
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{variant: 'body2'}}>{childItem.text}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
      <nav> {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: navbarBg },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </AppBar>
  );
};

export default Navbar;
